import * as THREE from 'three';
import { WAVE_COUNT, type WaveField } from '../../sim/waves';
import { ATMO_GLSL, atmoUniforms } from '../atmosphere';
import { DetailWaves, DETAIL_TILE } from './detail';
import { FoamSim, FOAM_SIZE, whitecapThreshold } from './foam';
import { FullscreenPass, makeTarget } from '../gpu';

// The ocean surface: a camera-centred radial grid displaced by the same
// spectral Gerstner waves the physics uses. Components too short for the local
// vertex spacing are faded out of the geometry but kept in the per-pixel
// normal, and anything shorter than a pixel becomes roughness instead, so the
// sea stays sharp up close and alias-free at the horizon.

const SEGMENTS = 256;
const R_MAX = 26000;

const VERT = /* glsl */ `
#include <common>
#include <shadowmap_pars_vertex>
uniform vec4 uWaveA[${WAVE_COUNT}];
uniform vec4 uWaveB[${WAVE_COUNT}];
uniform float uAngStep;
uniform mat4 uShipInv;
uniform float uShipSpeed;
out vec2 vP0;
out vec3 vWorld;
out float vHeight;

float bowWave(vec3 sl, float speed) {
  if (abs(sl.z) > 14.0 || sl.x < -32.0 || sl.x > 30.0) return 0.0;
  float ax = sl.x > 1.5 ? (sl.x - 1.5) / 16.2 : (1.5 - sl.x) / 18.3;
  float hb = 5.3 * sqrt(max(0.0, 1.0 - pow(min(ax, 1.0), 2.2)));
  float d = max(abs(sl.z) - hb, 0.0);
  float s2 = min(speed * speed, 60.0);
  float bow = exp(-d / 1.4) * smoothstep(8.0, 15.5, sl.x) * (1.0 - smoothstep(17.0, 20.5, sl.x)) * 0.024;
  float trough = -exp(-d / 3.0) * (1.0 - smoothstep(0.0, 12.0, abs(sl.x - 1.0))) * 0.009;
  float stern = exp(-d / 2.0) * (1.0 - smoothstep(-15.0, -8.0, sl.x)) * smoothstep(-24.0, -17.0, sl.x) * 0.013;
  return (bow + trough + stern) * s2;
}

void main() {
  vec2 p0 = position.xz;
  float r = length(p0);
  float spacing = max(r, 1.5) * uAngStep;
  vec3 d = vec3(0.0);
  for (int i = 0; i < ${WAVE_COUNT}; i++) {
    vec4 a = uWaveA[i];
    vec4 b = uWaveB[i];
    float fade = smoothstep(spacing * 3.0, spacing * 7.0, b.z);
    if (fade <= 0.0 || a.w <= 0.0) continue;
    float th = a.z * dot(a.xy, p0) + b.x;
    float s = sin(th), c = cos(th);
    d.y += a.w * c * fade;
    d.xz -= b.y * a.xy * s * fade;
  }
  vec3 transformed = vec3(p0.x + d.x, d.y, p0.y + d.z);
  vec4 worldPosition = modelMatrix * vec4(transformed, 1.0);
  vec3 sl = (uShipInv * worldPosition).xyz;
  worldPosition.y += bowWave(sl, uShipSpeed);
  vWorld = worldPosition.xyz;
  vHeight = worldPosition.y;
  vP0 = p0;
  vec4 mvPosition = viewMatrix * worldPosition;
  gl_Position = projectionMatrix * mvPosition;
  #include <shadowmap_vertex>
}
`;

const FRAG = /* glsl */ `
#include <common>
#include <packing>
#include <lights_pars_begin>
#include <shadowmap_pars_fragment>
#include <shadowmask_pars_fragment>
${ATMO_GLSL}
uniform vec4 uWaveA[${WAVE_COUNT}];
uniform vec4 uWaveB[${WAVE_COUNT}];
uniform sampler2D uDetail;
uniform vec2 uDetailOffset;
uniform sampler2D uFoamSim;
uniform vec2 uFoamOffset;
uniform sampler2D uFoamTex;
uniform sampler2D uRefraction;
uniform sampler2D uSceneDepth;
uniform vec2 uRes;
uniform vec2 uNearFar;
uniform float uRain;
uniform float uTime;
uniform float uWhitecap;
uniform float uSeaHeight;
uniform float uWindSpeed;
uniform vec2 uWindDir;
uniform vec2 uFoamTexOffset;
uniform float uNight;
in vec2 vP0;
in vec3 vWorld;
in float vHeight;

float h12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

vec2 rainRipples(vec2 p, float t) {
  vec2 slope = vec2(0.0);
  for (int l = 0; l < 3; l++) {
    float sc = l == 0 ? 2.1 : (l == 1 ? 3.3 : 4.7);
    vec2 q = p * sc + float(l) * 7.31;
    vec2 id = floor(q);
    vec2 f = fract(q) - 0.5;
    float h = h12(id + float(l) * 3.7);
    float tt = fract(t * (0.9 + h * 0.6) + h * 13.0);
    vec2 c = (vec2(h12(id + 1.3), h12(id + 2.7)) - 0.5) * 0.5;
    vec2 dd = f - c;
    float rr = length(dd);
    float ring = tt * 0.48;
    float w = sin((rr - ring) * 55.0) * exp(-abs(rr - ring) * 22.0) * (1.0 - tt) * (1.0 - tt);
    slope += dd / max(rr, 1e-3) * w * 0.5;
  }
  return slope;
}

float D_GGX(float NoH, float a) {
  float a2 = a * a;
  float d = NoH * NoH * (a2 - 1.0) + 1.0;
  return a2 / (PI * d * d);
}
float V_Smith(float NoV, float NoL, float a) {
  float k = a * 0.5;
  return 0.25 / ((NoV * (1.0 - k) + k) * (NoL * (1.0 - k) + k));
}

void main() {
  vec3 toCam = cameraPosition - vWorld;
  float dist = length(toCam);
  vec3 V = toCam / dist;
  vec2 p0 = vP0;
  float fp = max(length(dFdx(p0)), length(dFdy(p0)));

  // Analytic slopes and Jacobian from the spectral waves.
  float dydx = 0.0, dydz = 0.0, jxx = 0.0, jzz = 0.0, jxz = 0.0;
  float lost = 0.0;
  for (int i = 0; i < ${WAVE_COUNT}; i++) {
    vec4 a = uWaveA[i];
    vec4 b = uWaveB[i];
    float ak = a.w * a.z;
    float fade = smoothstep(fp * 2.0, fp * 6.0, b.z);
    lost += (1.0 - fade) * ak * ak * 0.5;
    if (fade <= 0.0 || a.w <= 0.0) continue;
    float th = a.z * dot(a.xy, p0) + b.x;
    float s = sin(th), c = cos(th);
    dydx -= ak * a.x * s * fade;
    dydz -= ak * a.y * s * fade;
    float qk = b.y * a.z * c * fade;
    jxx -= qk * a.x * a.x;
    jzz -= qk * a.y * a.y;
    jxz -= qk * a.x * a.y;
  }

  // Wind ripples: two differently scaled, rotated samples of the detail tile.
  vec2 uv1 = p0 / ${DETAIL_TILE.toFixed(1)} + uDetailOffset;
  vec2 uv2 = mat2(0.8, -0.6, 0.6, 0.8) * (p0 / ${(DETAIL_TILE * 0.43).toFixed(3)}) + uDetailOffset * 2.3;
  vec3 d1 = texture(uDetail, uv1).xyz;
  vec3 d2 = texture(uDetail, uv2).xyz;
  vec2 s2 = mat2(0.8, 0.6, -0.6, 0.8) * d2.xy;
  float detailFade = 1.0 - smoothstep(0.08, 1.2, fp);
  vec2 detail = (d1.xy + s2 * 0.6) * detailFade;
  lost += (1.0 - detailFade) * 0.012 * clamp(uWindSpeed / 9.0, 0.1, 1.3);
  dydx += detail.x;
  dydz += detail.y;
  if (uRain > 0.01) {
    vec2 rr = rainRipples(p0 + uDetailOffset * ${DETAIL_TILE.toFixed(1)}, uTime) * uRain * (1.0 - smoothstep(10.0, 60.0, dist));
    dydx += rr.x;
    dydz += rr.y;
  }

  float nx = dydz * jxz - (1.0 + jzz) * dydx;
  float ny = (1.0 + jzz) * (1.0 + jxx) - jxz * jxz;
  float nz = jxz * dydx - dydz * (1.0 + jxx);
  vec3 N = normalize(vec3(nx, max(ny, 0.05), nz));
  float J = ny;
  // Keep grazing views from showing back-facing normals.
  N = normalize(N + vec3(0.0, max(0.0, -dot(N, V)) * 1.2, 0.0));

  float rough = clamp(0.06 + sqrt(lost) * 0.9 + fp * 0.004, 0.06, 0.55);

  // ---- foam
  vec2 fuv = (p0 + uFoamOffset) / ${FOAM_SIZE.toFixed(1)} + 0.5;
  float inMap = step(0.0, fuv.x) * step(fuv.x, 1.0) * step(0.0, fuv.y) * step(fuv.y, 1.0);
  float mapEdge = inMap * (1.0 - smoothstep(0.42, 0.5, max(abs(fuv.x - 0.5), abs(fuv.y - 0.5))));
  vec4 fs = texture(uFoamSim, fuv) * mapEdge;
  float instant = (1.0 - smoothstep(uWhitecap - 0.3, uWhitecap, J));
  float whitecap = mix(instant, fs.r, mapEdge);
  vec2 fuvT = (p0 + uFoamTexOffset) / 9.0;
  float pat = texture(uFoamTex, fuvT).r * 0.6 + texture(uFoamTex, fuvT * 0.37 + 0.31).g * 0.4;
  float patFar = mix(pat, 0.55, smoothstep(0.02, 0.4, fp));
  float foamAmt = clamp(max(whitecap * 0.9, fs.g), 0.0, 1.0);
  float foam = smoothstep(1.0 - foamAmt, 1.0 - foamAmt + 0.35, patFar) * clamp(foamAmt * 1.6, 0.0, 1.0);
  // Streaks of spume along the wind in a gale.
  vec2 along = vec2(dot(p0, uWindDir), dot(p0, vec2(-uWindDir.y, uWindDir.x)));
  float lanes = texture(uFoamTex, vec2(along.x / 70.0, along.y / 5.0) + uFoamTexOffset / 70.0).b;
  float patches = texture(uFoamTex, (p0 + uFoamTexOffset) / 140.0 + 0.23).g;
  float streak = smoothstep(14.0, 22.0, uWindSpeed) * smoothstep(0.6, 0.92, lanes) * smoothstep(0.35, 0.75, patches);
  foam = max(foam, streak * 0.3 * (1.0 - smoothstep(0.1, 0.8, fp)));
  float aerated = max(fs.b, whitecap * 0.5);

  // ---- lighting
  vec3 L = uSunDir;
  float shadow = getShadowMask();
  float cloud = atmoCloudShadow(vWorld);
  vec3 sunI = uSunColor * shadow * cloud;
  float NoV = max(dot(N, V), 1e-3);
  float NoL = max(dot(N, L), 0.0);
  float F = 0.02 + 0.98 * pow(1.0 - NoV, 5.0);
  F *= mix(1.0, 0.55, clamp(rough * 1.5, 0.0, 1.0));

  vec3 R = reflect(-V, N);
  if (R.y < 0.0) R = normalize(vec3(R.x, -R.y * 0.35, R.z));
  vec3 refl = textureLod(uSkyCube, R, clamp(rough * 9.0, 0.0, 6.0)).rgb;

  vec3 H = normalize(L + V);
  float NoH = max(dot(N, H), 0.0);
  float a = rough * rough;
  vec3 spec = sunI * D_GGX(NoH, max(a, 0.0025)) * V_Smith(NoV, max(NoL, 1e-3), max(a, 0.0025)) * NoL * (0.02 + 0.98 * pow(1.0 - max(dot(V, H), 0.0), 5.0));
  // A mirror can't be brighter than the sun disk times Fresnel; keep the
  // glint inside half-float range.
  spec = min(spec, vec3(120.0));

  // Water body: what is behind (refracted, absorbed) plus light scattered
  // back out of the water column.
  vec2 suv = gl_FragCoord.xy / uRes;
  float sceneZ = -perspectiveDepthToViewZ(texture(uSceneDepth, suv).r, uNearFar.x, uNearFar.y);
  float waterZ = dist;
  float thick = max(sceneZ - waterZ, 0.0);
  vec2 ruv = suv + N.xz * 0.045 * clamp(thick / 3.0, 0.0, 1.0) / max(1.0, dist * 0.08);
  float sceneZr = -perspectiveDepthToViewZ(texture(uSceneDepth, ruv).r, uNearFar.x, uNearFar.y);
  if (sceneZr < waterZ) ruv = suv;
  else thick = max(sceneZr - waterZ, 0.0);
  vec3 behind = texture(uRefraction, ruv).rgb;
  vec3 absorb = exp(-vec3(0.46, 0.075, 0.042) * thick * 1.6);
  float amb = dot(uSkyAmbient, vec3(0.3, 0.5, 0.2));
  vec3 irr = sunI * max(L.y, 0.0) + uSkyAmbient;
  vec3 deep = vec3(0.0020, 0.0105, 0.0175) * irr + vec3(0.0006, 0.004, 0.007) * amb;
  vec3 body = behind * absorb + deep * (1.0 - absorb);
  // Aerated water under foam and in wakes glows turquoise.
  body += vec3(0.012, 0.05, 0.05) * irr * aerated;
  // Subsurface light through thin crests facing away from the sun.
  float crest = clamp((vHeight - uSeaHeight * 0.1) / max(uSeaHeight, 0.3), 0.0, 1.0);
  float back = pow(clamp(dot(V, -L) * 0.5 + 0.5, 0.0, 1.0), 5.0);
  float thin = pow(clamp(1.0 - N.y, 0.0, 1.0), 0.6);
  body += vec3(0.02, 0.16, 0.14) * sunI * crest * (back * 1.6 + 0.12) * (0.3 + thin);

  vec3 col = mix(body, refl, F) + spec * (1.0 - foam);
  // Foam: a rough white scatterer.
  vec3 foamCol = vec3(0.86, 0.9, 0.92) * (sunI * (NoL * 0.6 + 0.25) + uSkyAmbient * 1.1) / PI;
  col = mix(col, foamCol, foam);
  col += vec3(0.75, 0.8, 1.0) * uFlash.w * 0.05 * (0.2 + F);
  col = applyAtmosphere(col, vWorld - cameraPosition);
  if (any(isnan(col)) || any(isinf(col))) col = vec3(0.0);
  gl_FragColor = vec4(min(col, vec3(30000.0)), 1.0);
}
`;

const FOAM_TEX_FRAG = /* glsl */ `
in vec2 vUv;
vec2 h22(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}
// Tileable cellular noise: bubbles and lace.
float cells(vec2 uv, float n) {
  vec2 p = uv * n;
  vec2 id = floor(p), f = fract(p);
  float d1 = 8.0, d2 = 8.0;
  for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
    vec2 o = vec2(x, y);
    vec2 c = mod(id + o, n);
    vec2 r = o + h22(c) - f;
    float d = dot(r, r);
    if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) d2 = d;
  }
  return sqrt(d2) - sqrt(d1);
}
void main() {
  float lace = 1.0 - smoothstep(0.0, 0.16, cells(vUv, 7.0));
  float bub = smoothstep(0.02, 0.18, cells(vUv, 23.0));
  float fine = cells(vUv, 61.0);
  float r = clamp(lace * 0.65 + (1.0 - bub) * 0.25 + fine * 0.5, 0.0, 1.0);
  float g = clamp(1.0 - smoothstep(0.0, 0.3, cells(vUv + 0.37, 5.0)) * 0.7 + fine * 0.4, 0.0, 1.0);
  float b = clamp(cells(vec2(vUv.x, vUv.y * 0.25), 12.0) * 1.6, 0.0, 1.0);
  gl_FragColor = vec4(r, g, b, 1.0);
}
`;

function buildGrid(): THREE.BufferGeometry {
  const pos: number[] = [];
  const idx: number[] = [];
  const growth = 1 + (2 * Math.PI) / SEGMENTS;
  const radii = [0];
  let r = 0.5;
  while (r < R_MAX) {
    radii.push(r);
    r *= growth;
  }
  radii.push(R_MAX);
  // Centre vertex.
  pos.push(0, 0, 0);
  for (let i = 1; i < radii.length; i++) {
    const rr = radii[i]!;
    for (let s = 0; s < SEGMENTS; s++) {
      const a = (s / SEGMENTS) * Math.PI * 2 + (i % 2) * (Math.PI / SEGMENTS);
      pos.push(Math.cos(a) * rr, 0, Math.sin(a) * rr);
    }
  }
  for (let s = 0; s < SEGMENTS; s++) idx.push(0, 1 + ((s + 1) % SEGMENTS), 1 + s);
  for (let i = 1; i < radii.length - 1; i++) {
    const a0 = 1 + (i - 1) * SEGMENTS;
    const b0 = 1 + i * SEGMENTS;
    for (let s = 0; s < SEGMENTS; s++) {
      const s1 = (s + 1) % SEGMENTS;
      const a = a0 + s, b = a0 + s1;
      const c = b0 + s, d = b0 + s1;
      if (i % 2 === 1) idx.push(a, b, d, a, d, c);
      else idx.push(a, b, c, b, d, c);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  return g;
}

export interface OceanFrame {
  camera: THREE.PerspectiveCamera;
  waves: WaveField;
  time: number;
  dt: number;
  windSpeed: number;
  windX: number;
  windZ: number;
  rain: number;
  shipX: number;
  shipZ: number;
  shipMatrixInverse: THREE.Matrix4;
  shipVelLocal: THREE.Vector3;
  shipHeave: number;
  night: number;
}

export class Ocean {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.ShaderMaterial;
  readonly detail = new DetailWaves();
  readonly foam = new FoamSim();
  private readonly foamTex = makeTarget(512, 512, {
    type: THREE.UnsignedByteType,
    wrapS: THREE.RepeatWrapping,
    wrapT: THREE.RepeatWrapping,
    minFilter: THREE.LinearMipmapLinearFilter,
    generateMipmaps: true,
  });
  private readonly waveA = new Float32Array(WAVE_COUNT * 4);
  private readonly waveB = new Float32Array(WAVE_COUNT * 4);
  private readonly aVec: THREE.Vector4[] = [];
  private readonly bVec: THREE.Vector4[] = [];

  constructor(renderer: THREE.WebGLRenderer) {
    for (let i = 0; i < WAVE_COUNT; i++) {
      this.aVec.push(new THREE.Vector4());
      this.bVec.push(new THREE.Vector4());
    }
    const bake = new FullscreenPass(FOAM_TEX_FRAG, {});
    bake.render(renderer, this.foamTex);
    bake.dispose();
    renderer.setRenderTarget(null);

    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      lights: true,
      uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.lights, {}]),
    });
    Object.assign(this.material.uniforms, atmoUniforms, {
      uWaveA: { value: this.aVec },
      uWaveB: { value: this.bVec },
      uAngStep: { value: (2 * Math.PI) / SEGMENTS },
      uShipInv: { value: new THREE.Matrix4() },
      uShipSpeed: { value: 0 },
      uDetail: { value: this.detail.target.texture },
      uDetailOffset: { value: new THREE.Vector2() },
      uFoamSim: { value: this.foam.texture },
      uFoamOffset: { value: new THREE.Vector2() },
      uFoamTex: { value: this.foamTex.texture },
      uFoamTexOffset: { value: new THREE.Vector2() },
      uRefraction: { value: null },
      uSceneDepth: { value: null },
      uRes: { value: new THREE.Vector2(1, 1) },
      uNearFar: { value: new THREE.Vector2(0.25, 50000) },
      uRain: { value: 0 },
      uTime: { value: 0 },
      uWhitecap: { value: 0.3 },
      uSeaHeight: { value: 1 },
      uWindSpeed: { value: 5 },
      uWindDir: { value: new THREE.Vector2(1, 0) },
      uNight: { value: 0 },
    });
    this.mesh = new THREE.Mesh(buildGrid(), this.material);
    this.mesh.frustumCulled = false;
    this.mesh.receiveShadow = true;
    this.mesh.renderOrder = -1;
  }

  /** Bind the opaque-pass colour and depth for refraction. */
  setSceneTextures(color: THREE.Texture, depth: THREE.Texture, w: number, h: number): void {
    const u = this.material.uniforms;
    u.uRefraction!.value = color;
    u.uSceneDepth!.value = depth;
    u.uRes!.value.set(w, h);
  }

  update(renderer: THREE.WebGLRenderer, f: OceanFrame): void {
    const cam = f.camera;
    // Centre the grid under the camera, snapped to damp vertex swimming.
    const snap = 0.5;
    const ox = Math.round(cam.position.x / snap) * snap;
    const oz = Math.round(cam.position.z / snap) * snap;
    this.mesh.position.set(ox, 0, oz);
    this.mesh.updateMatrixWorld();
    f.waves.packUniforms(ox, oz, this.waveA, this.waveB);
    for (let i = 0; i < WAVE_COUNT; i++) {
      this.aVec[i]!.fromArray(this.waveA, i * 4);
      this.bVec[i]!.fromArray(this.waveB, i * 4);
    }
    this.detail.update(renderer, f.time, f.windSpeed, f.windX, f.windZ);
    this.foam.update(renderer, f.dt, f.time, f.waves, f.windSpeed, f.shipX, f.shipZ, f.shipMatrixInverse, f.shipVelLocal, f.shipHeave);

    const u = this.material.uniforms;
    const tile = DETAIL_TILE;
    u.uDetailOffset!.value.set(mod(ox, tile) / tile, mod(oz, tile) / tile);
    u.uFoamSim!.value = this.foam.texture;
    u.uFoamOffset!.value.set(ox - this.foam.center.x, oz - this.foam.center.y);
    u.uFoamTexOffset!.value.set(mod(ox, 9 * 100), mod(oz, 9 * 100));
    u.uShipInv!.value.copy(f.shipMatrixInverse);
    u.uShipSpeed!.value = Math.max(0, f.shipVelLocal.x);
    u.uNearFar!.value.set(cam.near, cam.far);
    u.uRain!.value = f.rain;
    u.uTime!.value = f.time;
    u.uWhitecap!.value = whitecapThreshold(f.windSpeed);
    u.uSeaHeight!.value = Math.max(0.3, f.waves.significantHeight * 0.5);
    u.uWindSpeed!.value = f.windSpeed;
    u.uWindDir!.value.set(f.windX, f.windZ);
    u.uNight!.value = f.night;
  }
}

function mod(a: number, b: number): number {
  return ((a % b) + b) % b;
}
